import React, { PureComponent, Fragment } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { LocationArrowIcon } from 'shared/icons';
import Dropdown from '@fountain/ui-kit/components/dropdown';
import OverlaySpinner from '@fountain/ui-kit/components/overlay-spinner';
import ClearableInput from '@fountain/ui-kit/components/clearable-input';
import notify from '@fountain/ui-kit/components/notifications';
import { loadLibrary, isLibraryLoaded, LIBRARIES } from 'helpers/libs-loader';
import GooglePlacesAPI from '../../../v1/helpers/google-places';

import './styles.pcss';

const LOCATION_TEXT_UNKNOWN = 'Unknown Location';

export default class LocationPicker extends PureComponent {
  static propTypes = {
    apiKey: PropTypes.string,
    className: PropTypes.string,
    disabled: PropTypes.bool,
    id: PropTypes.string.isRequired,
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    value: PropTypes.shape({
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    required: PropTypes.bool,
    translations: PropTypes.shape({
      view: PropTypes.string,
      cityOrZipCodePlaceholder: PropTypes.string,
      clearAllSelections: PropTypes.string,
      jobOpenings: PropTypes.string,
      location: PropTypes.string,
      positions: PropTypes.string,
      radius: PropTypes.string,
      selectPosition: PropTypes.string,
      nonePositions: PropTypes.string,
      showRemoteJobsOnly: PropTypes.string,
      locationIsRequired: PropTypes.string,
      noResultsInRadius: PropTypes.string,
      useCurrentLocation: PropTypes.string,
      hour: PropTypes.string,
    }).isRequired,
    requestLocationOnLoad: PropTypes.bool,
  }

  static defaultProps = {
    apiKey: undefined,
    className: undefined,
    disabled: false,
    name: undefined,
    value: undefined,
    required: false,
    requestLocationOnLoad: false,
  }

  constructor(props) {
    super(props);

    this.state = {
      address: '',
      addressIsDirty: false,
      currentPosition: null,
      geolocationFailed: false,
      geolocationInProcess: false,
      libIsLoading: !isLibraryLoaded(LIBRARIES.GOOGLE_MAPS),
      predictions: [],
    };
  }

  componentDidMount() {
    if (!this.props.apiKey) {
      return;
    }
    loadLibrary(LIBRARIES.GOOGLE_MAPS, this.props.apiKey)
      .then((google) => {
        this._google = google;

        // Use the new Google Places API
        this.newPlacesAPI = new GooglePlacesAPI();
        this.newPlacesAPI.setApiKey(this.props.apiKey);
        this.sessionToken = GooglePlacesAPI.generateSessionToken();

        this._geocoder = new this._google.maps.Geocoder();
        const requestGeolocation = this.props.requestLocationOnLoad && this._geolocationSupported();

        const loadAddress = () => {
          if (requestGeolocation) return this._setAddressByGeolocation();
          return this._loadAddress(this.props.value);
        };

        if (this.state.libIsLoading) {
          return this.setState({ libIsLoading: false }, () => loadAddress());
        }

        return loadAddress();
      })
      .catch(() => {
        notify('Error while loading map.', 'error');
      });
  }

  _loadAddress = (coordinates) => {
    if (coordinates) {
      this._getAddressByCoords(coordinates)
        .then(({ address }) => this.setState({ address }))
        .catch(() => this.setState({ address: LOCATION_TEXT_UNKNOWN }));
    }
  }

  _geolocationSupported = () => !!navigator.geolocation

  _requestGeoLocation = () => new Promise((resolve, reject) => {
    if (this.state.currentPosition) {
      resolve(this.state.currentPosition);
      return;
    }

    this.setState(
      { geolocationInProcess: true },
      () => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const currentPosition = {
              latitude: position.coords.latitude,
              longitude: position.coords.longitude,
            };

            this.setState({ geolocationInProcess: false, currentPosition }, () => resolve(currentPosition));
          },
          () => {
            this.setState({ geolocationInProcess: false, geolocationFailed: true });
            reject();
          }
        );
      }
    );
  })

  _setAddressByGeolocation = () => this._requestGeoLocation()
    .then(async (position) => {
      await this._loadAddress(position);
      this._change({ address: this.state.address, position });
    })
    .catch(() => notify('You blocked Geolocation API', 'error'))

  _handleInputChange = inputValue => this.setState({
    address: inputValue,
    addressIsDirty: true,
    predictions: !inputValue ? [] : this.state.predictions,
  }, () => {
    if (!inputValue) {
      this._change({ position: undefined, address: undefined });
      this._dropdownComponent.close();
    } else {
      this._dropdownComponent.open();
      this._getPredictions(inputValue);
    }
  })

  // select first autocomplete suggestion if user does not make a selection
  _handleInputBlur = () => {
    if (this.state.predictions.length > 0 && !this._dropdownComponent.state.isOpen && !!this.state.addressIsDirty) {
      this._getCoordsByPlaceId(this.state.predictions[0].place_id);
    }
  }

  // types '(cities)' returns results that match 'locality' or 'administrative_area_level_3'
  _getPredictions = (query) => {
    // Use new Google Places API
    if (this.newPlacesAPI) {
      this.newPlacesAPI.autocomplete(query, {
        sessionToken: this.sessionToken,
        languageCode: 'en',
        regionCode: 'US',
        includedRegionCodes: ['US'],
      }).then((response) => {
        const predictions = response.suggestions || [];
        this.setState({
          predictions: predictions.map(suggestion => ({
            description: (suggestion.placePrediction && suggestion.placePrediction.text && suggestion.placePrediction.text.text) || '',
            place_id: (suggestion.placePrediction && suggestion.placePrediction.placeId) || '',
            id: (suggestion.placePrediction && suggestion.placePrediction.placeId) || '',
            structured_formatting: {
              main_text: (suggestion.placePrediction && suggestion.placePrediction.structuredFormat && suggestion.placePrediction.structuredFormat.mainText && suggestion.placePrediction.structuredFormat.mainText.text) || '',
              secondary_text: (suggestion.placePrediction && suggestion.placePrediction.structuredFormat && suggestion.placePrediction.structuredFormat.secondaryText && suggestion.placePrediction.structuredFormat.secondaryText.text) || '',
            },
          })),
        });
      }).catch((error) => {
        console.error('New Places API error:', error);
        this.setState({ predictions: [] });
      });
    } else {
      // Use old Google Places API (backward compatibility)
      this._autocompleteService.getPlacePredictions(
        { input: query, types: ['(regions)'] },
        (predictions, status) => {
          const { OK } = this._google.maps.places.PlacesServiceStatus;
          this.setState({ predictions: status === OK ? predictions : [] });
        }
      );
    }
  }

  _getCoordsByPlaceId = placeId => this._geocoder.geocode(
    { placeId },
    (results, status) => {
      const { OK } = this._google.maps.GeocoderStatus;

      if (!results.length || status !== OK) {
        return;
      }

      this._change({
        position: {
          latitude: results[0].geometry.location.lat(),
          longitude: results[0].geometry.location.lng(),
        },
        address: results[0].formatted_address,
      });
    }
  )

  _getAddressByCoords = coords => new Promise((resolve, reject) => this._geocoder.geocode(
    { location: { lat: coords.latitude, lng: coords.longitude } },
    (results, status) => {
      const { OK } = this._google.maps.GeocoderStatus;

      if (!results.length || status !== OK) {
        reject();
        return;
      }

      // eg. url with no locality in the types array http://localhost:3000/c/test_account/fountain/CA?distance=25km&lat=29.8637408&lon=-95.7895779
      const filteredAddressByLocality = results.find(({ types }) => types.includes('locality'));
      const cityState = filteredAddressByLocality ? filteredAddressByLocality.formatted_address : results[0].formatted_address;

      const value = {
        position: {
          latitude: results[0].geometry.location.lat(),
          longitude: results[0].geometry.location.lng(),
        },
        address: cityState,
      };
      resolve(value);
    }
  ))

  _change = ({ position, address }) => this.setState({ address, addressIsDirty: false }, () => this.props.onChange(position));

  _renderItems = ({ close }) => {
    const {
      geolocationFailed,
      predictions,
    } = this.state;

    return (
      <Fragment>
        {this._geolocationSupported() && (
          <div
            className={cn('location-picker__menu-item', geolocationFailed && 'location-picker__menu-item_disabled')}
            onClick={() => {
              this._setAddressByGeolocation();
              close();
            }}
            role="presentation"
          >
            <LocationArrowIcon className="location-picker__menu-item-icon" />
            {this.props.translations.useCurrentLocation}
          </div>
        )}

        {predictions.map(item => (
          <div
            className="location-picker__menu-item"
            key={item.id}
            onClick={() => {
              close();
              this._getCoordsByPlaceId(item.place_id);
            }}
            role="presentation"
          >
            {item.description}
          </div>
        ))}
      </Fragment>
    );
  }

  render() {
    const {
      className,
      disabled,
      id,
      name,
      translations: { cityOrZipCodePlaceholder, locationIsRequired },
      required,
    } = this.props;
    const {
      address,
      geolocationInProcess,
      libIsLoading,
    } = this.state;

    const locationRequiredClass = required ? 'invalid-input-error-border' : '';

    return (
      <Dropdown
        ref={component => (this._dropdownComponent = component)}
        className={cn(className, 'location-picker')}
        classNameMenu="location-picker__menu"
        disabled={disabled || libIsLoading || geolocationInProcess}
        fullWidthMenu
        toggle={
          <OverlaySpinner
            className="location-picker__toggle"
            show={geolocationInProcess}
            size="small"
          >
            <ClearableInput
              className={`location-picker__input ${locationRequiredClass}`}
              disabled={disabled || libIsLoading}
              id={id}
              innerRef={node => (this._inputNode = node)}
              name={name || id}
              onBlur={this._handleInputBlur}
              onChange={this._handleInputChange}
              placeholder={cityOrZipCodePlaceholder}
              value={address}
              fullWidth
            />
            { required ? <div className="form-error sourcing-wizard-goals-and-details-step__errors">{locationIsRequired}</div> : null }
          </OverlaySpinner>
        }
      >
        {this._renderItems}
      </Dropdown>
    );
  }
}
